/**
 * Chat Module Constants
 *
 * Constantes centralizadas para o módulo de chat
 */

// Design System Colors (baseado no mockup)
export const COLORS = {
  primary: '#F4CC04',
  primaryHover: '#d4b104',
  background: {
    main: '#242424',        // Fundo principal do chat
    dark: '#0a0a0a',        // Preto puro (sidebar, etc)
    card: '#1a1a1a',        // Balão de mensagem da TAIS
    input: '#1a1a1a',       // Fundo do input/textarea (mesmo tom do sidebar e balão IA)
  },
  text: {
    white: '#FFFFFF',
    gray: '#CCCCCC',
    darkGray: '#888888',
    muted: '#666666',
  },
  error: '#e53935',
  warning: '#ff9800',
  success: '#4CAF50',
  border: '#333333',
} as const;

// Chat Configuration
export const CHAT_CONFIG = {
  maxMessageLines: 10,
  debounceDelay: 500,
  autoScrollThreshold: 100,
  mobileAutoScrollThreshold: 50,
  retryLimit: 3,
} as const;

// File Upload Configuration
export const FILE_UPLOAD = {
  maxFiles: 5,
  maxFileSize: 10 * 1024 * 1024, // 10MB
  maxTotalSize: 20 * 1024 * 1024, // 20MB
  allowedExtensions: ['pdf', 'jpg', 'jpeg', 'png', 'docx', 'xlsx', 'txt', 'md', 'csv', 'json', 'xml'] as const,
  acceptAttribute: '.pdf,.jpg,.jpeg,.png,.docx,.xlsx,.txt,.md,.csv,.json,.xml',
} as const;

// Storage Keys
export const STORAGE_KEYS = {
  pendingChatQuestion: 'pendingChatQuestion',
  pendingChatFiles: 'pendingChatFiles',
  lastUsedAgentId: 'lastUsedAgentId',
} as const;

// Routes
export const CHAT_ROUTES = {
  newChat: '/chat',
  session: '/chat/:sessionId',
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  loadMessages: 'Falha ao carregar mensagens',
  sendMessage: 'Falha ao enviar mensagem',
  sendInitialMessage: 'Falha ao enviar mensagem inicial',
  retryMessage: 'Falha ao reenviar mensagem',
  retryLimit: 'Limite de tentativas atingido. Por favor, tente novamente mais tarde.',
  fileTooLarge: (fileName: string, maxSize: number) =>
    `O arquivo "${fileName}" excede o tamanho máximo de ${(maxSize / 1024 / 1024).toFixed(0)}MB.`,
  fileExtensionNotAllowed: (fileName: string, extensions: readonly string[]) =>
    `O arquivo "${fileName}" possui uma extensão não permitida. Extensões aceitas: ${extensions.join(', ').toUpperCase()}.`,
  maxFilesExceeded: (maxFiles: number) =>
    `Você pode anexar no máximo ${maxFiles} arquivos.`,
  totalSizeExceeded: (maxSize: number, currentSize: number) =>
    `O tamanho total dos arquivos não pode exceder ${(maxSize / 1024 / 1024).toFixed(0)}MB. Tamanho atual: ${(currentSize / 1024 / 1024).toFixed(2)}MB.`,
  generic: 'Ocorreu um erro. Tente novamente.',
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  messageSent: 'Mensagem enviada com sucesso',
  sessionCreated: 'Sessão criada com sucesso',
  fileCopied: 'Copiado!',
} as const;

// Loading Messages
export const LOADING_MESSAGES = {
  sendingMessage: 'Enviando...',
  loadingMessages: 'Carregando mensagens...',
  loadingAgents: 'Carregando agentes...',
  searching: 'Buscando...',
} as const;

// Avatar Configuration
export const AVATAR_CONFIG = {
  taisName: 'TAIS',
  taisInitial: 'T',
  userFallback: '👤',
} as const;

// Message Status
export const MESSAGE_STATUS = {
  sending: 'sending',
  sent: 'sent',
  error: 'error',
} as const;

// Artifact Configuration
export const ARTIFACT_CONFIG = {
  defaultSubtitle: 'Documento gerado pela TAIS',
  viewButtonText: 'VER ARTEFATO',
} as const;

// Welcome Messages - Professional and credible messages
export const WELCOME_MESSAGES = [
  'Como posso ajudar você hoje?',
  'No que posso ser útil?',
  'Estou pronto para ajudar. O que você precisa?',
  'Qual desafio podemos resolver juntos?',
  'Como posso colaborar com você hoje?',
  'Vamos começar. Qual é sua questão?',
  'Em que posso auxiliar você?',
  'Estou aqui para ajudar. Qual é o objetivo?',
  'O que você gostaria de realizar hoje?',
  'Como posso contribuir com seu trabalho?',
] as const;

// Utility Functions
export const getInitials = (name: string): string => {
  if (!name || name.length === 0) return 'U';
  const words = name.trim().split(/\s+/).filter(word => word.length > 0);
  if (words.length === 0) return 'U';
  if (words.length === 1) {
    // Single name: just first letter
    return words[0].charAt(0).toUpperCase();
  }
  // Multiple words: first letter of first word + first letter of last word
  const firstInitial = words[0].charAt(0).toUpperCase();
  const lastInitial = words[words.length - 1].charAt(0).toUpperCase();
  return firstInitial + lastInitial;
};
